/**
 * Design system colors.
 *
 * NOTE! This file is imported in the Tailwind config which does not go through
 * the main TypeScript setup. Importing things like utils is not possible here.
 *
 * https://zeroheight.com/1e5da6515/p/002f3f-colors
 */

// Define the full palette for easy reference and usage, any unused colors will
// be removed by minification at build time.
/* eslint-disable @typescript-eslint/no-unused-vars */

const black = '#111';
const white = '#fff';
const blue50 = '#f3f6fa';
const blue100 = '#eaeef5';
const blue200 = '#d8e0ed';
const blue300 = '#c0cce1';
const blue400 = '#a6b2d3';
const blue500 = '#8f9ac5';
const blue600 = '#777fb4';
const blue700 = '#686e9f';
const blue800 = '#535880';
const blue900 = '#484d67';
const blue950 = '#2a2c3c';
const emerald50 = '#ecfff5';
const emerald100 = '#d3ffe8';
const emerald200 = '#aaffd2';
const emerald300 = '#69ffb1';
const emerald400 = '#21ff88';
const emerald500 = '#00f267';
const emerald600 = '#00ca51';
const emerald700 = '#009e43';
const emerald800 = '#00853e';
const emerald900 = '#026532';
const emerald950 = '#003919';
const green50 = '#f1faeb';
const green100 = '#dff4d3';
const green200 = '#c1eaac';
const green300 = '#9bdb7b';
const green400 = '#77c952';
const green500 = '#509e2f';
const green600 = '#428a26';
const green700 = '#356a21';
const green800 = '#2c551f';
const green900 = '#28491e';
const green950 = '#12270c';
const grey50 = '#f6f6f6';
const grey100 = '#e7e7e7';
const grey200 = '#d1d1d1';
const grey300 = '#b0b0b0';
const grey400 = '#888888';
const grey500 = '#707070';
const grey600 = '#5d5d5d';
const grey700 = '#4f4f4f';
const grey800 = '#454545';
const grey900 = '#3d3d3d';
const grey950 = '#262626';
const lime50 = '#fffee4';
const lime100 = '#fffec5';
const lime200 = '#feff92';
const lime300 = '#f9ff53';
const lime400 = '#ecfb20';
const lime500 = '#c4d600';
const lime600 = '#a0b500';
const lime700 = '#798902';
const lime800 = '#5f6c08';
const lime900 = '#505b0c';
const lime950 = '#2a3300';
const orange50 = '#fffaeb';
const orange100 = '#fff2c6';
const orange200 = '#ffe388';
const orange300 = '#ffcf4a';
const orange400 = '#ffb81c';
const orange500 = '#ff9d0e';
const orange600 = '#ff8200';
const orange700 = '#cc5f02';
const orange800 = '#a1490b';
const orange900 = '#823e0c';
const orange950 = '#461d04';
const purple50 = '#fcf5fe';
const purple100 = '#f8e9fe';
const purple200 = '#f1d2fc';
const purple300 = '#e9aff8';
const purple400 = '#dd7ff3';
const purple500 = '#ca4ee7';
const purple600 = '#b12ecb';
const purple700 = '#9423a8';
const purple800 = '#7b1f89';
const purple900 = '#6d2077';
const purple950 = '#43084a';
const red50 = '#fdf5f3';
const red100 = '#fce9e7';
const red200 = '#f8d5d3';
const red300 = '#f3b1ae';
const red400 = '#ec8280';
const red500 = '#e05353';
const red600 = '#cb333b';
const red700 = '#ab2530';
const red800 = '#8f222e';
const red900 = '#7b202d';
const red950 = '#440d13';
const sky50 = '#f1f9fe';
const sky100 = '#e3f1fb';
const sky200 = '#c0e5f7';
const sky300 = '#88d1f1';
const sky400 = '#41b6e6';
const sky500 = '#2197d7';
const sky600 = '#0077c8';
const sky700 = '#015ea3';
const sky800 = '#065186';
const sky900 = '#0b446f';
const sky950 = '#072b4a';
const yellow50 = '#feffe7';
const yellow100 = '#faffc1';
const yellow200 = '#faff86';
const yellow300 = '#ffff41';
const yellow400 = '#fff30d';
const yellow500 = '#ffe500';
const yellow600 = '#d1aa00';
const yellow700 = '#a67a02';
const yellow800 = '#895f0a';
const yellow900 = '#744d0f';
const yellow950 = '#442904';

const FOREGROUND_FEEDBACK_COLORS = {
	error: red800,
	information: orange800,
	success: emerald900,
	successHover: '#05542B',
	successActive: '#064C28',
	disabled: grey600,
};

export const KEYWORD_COLORS = {
	current: 'currentColor',
	inherit: 'inherit',
	transparent: 'transparent',
	initial: 'initial',
	revert: 'revert',
	revertLayer: 'revert-layer',
	unset: 'unset',
};

// Globally used colors, for instance accent, fill, stroke, etc.
export const COLORS = {
	...KEYWORD_COLORS,
	...FOREGROUND_FEEDBACK_COLORS,
};

export const BACKGROUND_COLORS = {
	...KEYWORD_COLORS,

	base: white,
	baseHover: '#cfcfcf',
	baseActive: '#b8b8b8',
	inverse: grey950,
	positive: grey950,
	negative: white,
	strong: grey700,
	subtle: grey100,
	brand: red600,
	brandHover: '#a52c32',
	brandActive: '#93292e',
	brandDecorative: red600,
	action: emerald900,
	actionHover: '#05542b',
	actionActive: '#064c28',
	shadow: black,
	badge: emerald900,
	campaign: yellow500,
	julaclub: red800,
	julapro: grey950,
	saving: red700,
	news: orange400,

	// Feedback
	error: red100,
	information: orange100,
	success: green100,
	disabled: grey200,

	// Internal/custom
	tableStripe: grey50,
	shimmer: grey100,
	shimmerHighlight: grey50,
};

export const SHADOW_COLORS = {
	base: BACKGROUND_COLORS.shadow,
};

export const BORDER_COLORS = {
	...KEYWORD_COLORS,
	...FOREGROUND_FEEDBACK_COLORS,

	subtle: grey200,
	strong: grey700,
	focus: grey950,
	brand: red600,
	brandHover: '#a52c32',
	brandActive: '#93292e',
	brandDecorative: red700,
	inverse: white,

	// TODO: these are used in ProductTag for the flag 'corner' should be replaced with clip-path solution where bg- can be used instead
	campaign: '#ffe500',
	news: '#ffb81c',
};

export const ICON_COLORS = {
	...KEYWORD_COLORS,

	base: grey950,
	inverse: white,
	negative: white,
};

export const TEXT_COLORS = {
	...ICON_COLORS,
	...KEYWORD_COLORS,
	...FOREGROUND_FEEDBACK_COLORS,

	base: grey950,
	subtle: grey700,
	inverse: white,
	negative: white,
	brand: red600,
	news: orange950,
	reducedPrice: red600,
	pricetagCampaign: grey950,
	pricetagJulaclub: white,
	pricetagJulapro: white,
	pricetagSaving: white,
};

// Shouldn't be needed in code since they can be changed by editors.
export const CATEGORY_COLORS = {
	toolsMachines: sky400,
	constructionPaint: sky600,
	electricsLightning: orange400,
	clothesSafety: orange600,
	garden: lime500,
	leisure: green500,
	autoGarage: blue700,
	homeHousehold: purple900,
};

export type BorderColorName = keyof typeof BORDER_COLORS;
export type BackgroundColorName = keyof typeof BACKGROUND_COLORS;

function mapColors<
	ColorSet extends Record<string, string>,
	ColorSetKey extends keyof ColorSet,
>(colorSet: ColorSet, mapper: (key: ColorSetKey) => string) {
	const result = {} as Record<ColorSetKey, string>;
	Object.keys(colorSet).forEach((key) => {
		result[key] = mapper(key as ColorSetKey);
	});
	return result;
}

export const BACKGROUND_COLOR_CLASSES = mapColors(
	BACKGROUND_COLORS,
	(key) => `bg-${key}`,
);

export const TEXT_COLOR_CLASSES = mapColors(
	TEXT_COLORS,
	(key) => `text-${key}`,
);
